import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, TextField, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFiles,
  selectFormData,
  updateFileField,
  updateFormField,
} from '../../../Store/userSlice';
import {
  SECURITY_OPTIONS
} from "../../../utils/constants";
import DocumentUploadSection from "../../common/FileUpload/DocumentUploadSection";
import SelectField from "../../common/FormFields/SelectField";
import { base64ToFile } from "../../common/Util/Util";
import { requirementBriefValidation, salarySlipsValidation } from "../../common/ValidationRules/validationRules";

const RequirementBriefForm = React.forwardRef((props, ref) => {
  const {
    control,
    formState: { errors },
    trigger,
    setValue,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      fundRequirement: "",
      endUseOfFund: "",
      securityOffered: "",
      descriptionOfSecurity: "",
      salarySlips: null,
      joiningLetter: null,
      borrowerPanCard: null,
      gstDocx: null,
      as26: null,
      otherDocx: null,
    },
  });
  
  React.useImperativeHandle(ref, () => ({
    validateForm: async () => {
      const result = await trigger();
      return result;
    },
  }));
  
  const dispatch = useDispatch();
  const formData = useSelector(selectFormData);
  const files = useSelector(selectFiles);

  useEffect(() => {
    setValue("fundRequirement", formData?.fundRequirement);
    setValue("endUseOfFund", formData?.endUseOfFund);
    setValue("securityOffered", formData?.securityOffered);
    setValue("descriptionOfSecurity", formData?.descriptionOfSecurity);
    
    // For arrays, pass the whole array of files
    setValue("salarySlips", Array.isArray(files.salarySlips) ? 
      files.salarySlips.map(file => convertAndSetfile("salarySlips", file)) : null);
    
    setValue("joiningLetter", convertAndSetfile("joiningLetter", files.joiningLetter));
    
    setValue("borrowerPanCard", convertAndSetfile("borrowerPanCard", files.borrowerPanCard));
    setValue("gstDocx", convertAndSetfile("gstDocx", files.gstDocx));
    setValue("as26", convertAndSetfile("as26", files.as26));
    setValue("otherDocx", convertAndSetfile("otherDocx", files.otherDocx));
  }, [formData, files, setValue]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateFormField({ field: name, value: value }));
  };
  
  const convertAndSetfile = (fieldName, fileData) => {
    if (fileData?.base64 && fileData?.filename) {
      const fileObj = base64ToFile(fileData.base64, fileData.filename);
      return fileObj;
    } else if (fileData?.remote === true && (fileData?.url || fileData?.documentPath)) {
      return fileData;
    } else {
      return null;
    }
  };

  // Handle salary slips file selection
  const handleSalarySlipsSelect = async (selectedFiles) => {
    if (selectedFiles && selectedFiles.length > 0) {
      try {
        const filePromises = selectedFiles.map(file => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                base64: reader.result,
                filename: file.name
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        });

        const base64Files = await Promise.all(filePromises);
        dispatch(updateFileField({
          field: 'salarySlips',
          value: base64Files
        }));
      } catch (error) {
        console.error('Failed to process salary slip files:', error);
      }
    }
  };

  // Handle joining letter file selection
  const handleJoiningLetterSelect = async (file) => {
    if (file) {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          dispatch(updateFileField({
            field: 'joiningLetter',
            value: {
              base64: base64Data,
              filename: file.name,
            }
          }));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Failed to process joining letter file:', error);
      }
    }
  };

  // Handle entity PAN file selection
  const handleEntityPanSelect = async (file) => {
    if (file) {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          dispatch(updateFileField({
            field: 'borrowerPanCard',
            value: {
              base64: base64Data,
              filename: file.name,
            }
          }));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Failed to process entity PAN file:', error);
      }
    }
  };

  // Handle entity GSTIN file selection
  const handleEntityGstinSelect = async (file) => {
    if (file) {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          dispatch(updateFileField({
            field: 'gstDocx',
            value: {
              base64: base64Data,
              filename: file.name,
            }
          }));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Failed to process entity GSTIN file:', error);
      }
    }
  };

  // Handle 26AS file selection
  const handleTwentySixASSelect = async (file) => {
    if (file) {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          dispatch(updateFileField({
            field: 'as26',
            value: {
              base64: base64Data,
              filename: file.name,
            }
          }));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Failed to process 26AS file:', error);
      }
    }
  };
  
  const handleOtherDocsSelect = async (file) => {
    if (file) {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          dispatch(updateFileField({
            field: 'otherDocx',
            value: {
              base64: base64Data,
              filename: file.name,
            }
          }));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Failed to process other relevant docs file:', error);
      }
    }
  };
    
  const numberToWords = (num) => {
    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const convertLessThanOneThousand = (num) => {
      if (num === 0) return "";
      if (num < 20) return ones[num];
      const ten = Math.floor(num / 10);
      const unit = num % 10;
      return tens[ten] + (unit !== 0 ? " " + ones[unit] : "");
    };
    if (num === 0) return "Zero";
    let result = "";
    if (num >= 10000000) {
      result += convertLessThanOneThousand(Math.floor(num / 10000000)) + " Crore ";
      num %= 10000000;
    }
    if (num >= 100000) {
      result += convertLessThanOneThousand(Math.floor(num / 100000)) + " Lakh ";
      num %= 100000;
    }
    if (num >= 1000) {
      result += convertLessThanOneThousand(Math.floor(num / 1000)) + " Thousand ";
      num %= 1000;
    }
    if (num >= 100) {
      result += convertLessThanOneThousand(Math.floor(num / 100)) + " Hundred ";
      num %= 100;
    }
    if (num > 0) {
      if (result !== "") {
        result += "and ";
      }
      result += convertLessThanOneThousand(num);
    }
    return result.trim();
  }
  const getAmountInWords = (amount) => {
    if (!amount || isNaN(amount)) return "Zero Rupees";
    const loanAmount = Number(amount);
    return numberToWords(Math.floor(loanAmount)) + " Rupees";
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
        Step 4/4
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Mention the fund amount required<span style={{ color: "red" }}>*</span>
          </Typography>
          <Controller
            name="fundRequirement"
            control={control}
            rules={requirementBriefValidation.fundRequirement}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                type="num"
                placeholder="Enter fund requirement"
                error={!!errors?.fundRequirement}
                helperText={
                  errors.fundRequirement?.message
                    ? errors.fundRequirement?.message
                    : getAmountInWords(formData?.fundRequirement)
                }
                value={formData?.fundRequirement || ""}
                onKeyDown={(e) => {
                  if (["-", "e", "E", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = e?.target?.value;
                  if (value === "") {
                    field.onChange(e);
                    handleChange(e);
                    return;
                  }
                  if (/^\d{1,9}$/.test(value)) {
                    field?.onChange(e);
                    handleChange(e);
                  }
                }}
              />
            )}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Explain how the funds will be utilized<span style={{ color: "red" }}>*</span>
          </Typography>
          <Controller
            name="endUseOfFund"
            control={control}
            rules={requirementBriefValidation?.endUseOfFund}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={4}
                placeholder="Purchase of stocks, land, building, or machinery."
                error={!!errors?.endUseOfFund}
                helperText={errors?.endUseOfFund?.message}
                value={formData?.endUseOfFund || ""}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange(e);
                }}
              />
            )}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Provide details if any security is available, such as property
            address and estimated value<span style={{ color: "red" }}>*</span>
          </Typography>
          <Controller
            name="securityOffered"
            control={control}
            rules={requirementBriefValidation?.securityOffered}
            render={({ field: { onChange, onBlur, name, ref, ...restField } }) => (
              <SelectField
                name={name}
                onBlur={onBlur}
                ref={ref}
                {...restField}
                options={SECURITY_OPTIONS}
                value={restField.value || ""}
                error={!!errors?.securityOffered}
                helperText={errors?.securityOffered?.message}
                required
                onChange={(e) => {
                  onChange(e);
                  handleChange(e);
                }}
              />
            )}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Description of Security if available<span style={{ color: "red" }}>*</span>
          </Typography>
          <Controller
            name="descriptionOfSecurity"
            control={control}
            rules={requirementBriefValidation?.descriptionOfSecurity}
            render={({ field: { onChange, onBlur, name, ref, ...restField } }) => (
              <TextField
                name={name}
                onBlur={onBlur}
                ref={ref}
                {...restField}
                multiline
                rows={4}
                value={restField.value || ""}
                placeholder="e.g. Address of property and estimated value"
                error={!!errors?.descriptionOfSecurity}
                helperText={errors?.descriptionOfSecurity?.message}
                required
                fullWidth
                onChange={(e) => {
                  onChange(e);
                  handleChange(e);
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Requirement Brief (Individual User)
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <InfoOutlinedIcon sx={{ color: "#666" }} />
            <Typography variant="body2" color="text.secondary">
              Upload files in JPEG, PNG, or PDF format, up to 5 MB in size.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 3 }}>
            <DocumentUploadSection
              control={control}
              title="Upload last 3 months' salary slips"
              name="salarySlips"
              setValue={setValue}
              errors={errors?.salarySlips}
              helptext={errors?.salarySlips?.message}
              value={Array.isArray(files?.salarySlips) && files?.salarySlips.length > 0 ? 
                files?.salarySlips.map(file => convertAndSetfile("salarySlips", file)) : null}
              validationRule={salarySlipsValidation}
              multiple={true}
              onFileSelect={handleSalarySlipsSelect}
            />

            <DocumentUploadSection
              control={control}
              title="Joining letter"
              name="joiningLetter"
              setValue={setValue}
              errors={null}
              helptext={null}
              value={convertAndSetfile("joiningLetter", files?.joiningLetter)}
              onFileSelect={handleJoiningLetterSelect}
            />
          </Box>
          
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 3 }}>
            <DocumentUploadSection
              control={control}
              title="Entity PAN Card"
              name="borrowerPanCard"
              setValue={setValue}
              errors={null}
              helptext={null}
              value={convertAndSetfile("borrowerPanCard", files?.borrowerPanCard)}
              onFileSelect={handleEntityPanSelect}
            />
            <DocumentUploadSection
              control={control}
              title="Entity GSTIN Document"
              name="gstDocx"
              setValue={setValue}
              errors={null}
              helptext={null}
              value={convertAndSetfile("gstDocx", files?.gstDocx)}
              onFileSelect={handleEntityGstinSelect}
            />
            <DocumentUploadSection
              control={control}
              title="26AS"
              name="as26"
              setValue={setValue}
              errors={null}
              helptext={null}
              value={convertAndSetfile("as26", files?.as26)}
              onFileSelect={handleTwentySixASSelect}
            />
            <DocumentUploadSection
              control={control}
              title="Any other relevant docs"
              name="otherDocx"
              setValue={setValue}
              errors={null}
              helptext={null}
              value={convertAndSetfile("otherDocx", files?.otherDocx)}
              onFileSelect={handleOtherDocsSelect}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export default RequirementBriefForm;
